import { FormEvent, useEffect, useState } from 'react';
import { AuthUser, login, resetPassword } from '../../api/authApi';
import { AccessRequestOptions, fetchAccessRequestOptions, submitAccessRequest } from '../../api/accessApi';
import { APP_NAME, APP_TAGLINE } from '../../constants';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LoginProps {
  onLogin: (user: AuthUser) => void;
}

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function RequestAccessForm({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    username: '',
    password: '',
    department: '', // a department id
  });
  const [options, setOptions] = useState<AccessRequestOptions>({ departments: [], companies: [] }); // companies unused for now
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    fetchAccessRequestOptions()
      .then(setOptions)
      .catch(() => setError('Could not load departments. Refresh and try again.'));
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      setDone(
        await submitAccessRequest({
          username: form.username.trim(),
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          password: form.password,
          department_id: Number(form.department),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <CardContent className="flex flex-col gap-4 text-center">
        <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{done}</p>
        <p className="text-sm text-gray-600">
          You can sign in with the username and password you chose once an administrator approves it.
        </p>
        <Button type="button" className="w-full bg-gray-900 hover:bg-gray-800" onClick={onBack}>
          Back to sign in
        </Button>
      </CardContent>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="rq-name">Full name</Label>
            <Input id="rq-name" required value={form.full_name} onChange={set('full_name')} autoComplete="name" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="rq-email">Email</Label>
            <Input id="rq-email" type="email" required value={form.email} onChange={set('email')} autoComplete="email" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="rq-user">Username</Label>
            <Input
              id="rq-user"
              required
              minLength={3}
              pattern="[A-Za-z0-9._@-]+"
              title="Letters, numbers and . _ @ - only"
              value={form.username}
              onChange={set('username')}
              autoComplete="username"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="rq-pass">Password (min 6 characters)</Label>
              <Button
                type="button"
                variant="link"
                onClick={() => setShowPassword((s) => !s)}
                className="ml-auto h-auto p-0 text-sm"
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </div>
            <Input
              id="rq-pass"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={6}
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="rq-dept">Department</Label>
            <select id="rq-dept" required className={SELECT_CLASS} value={form.department} onChange={set('department')}>
              <option value="">Select department</option>
              {options.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>
      </CardContent>
      <CardFooter className="mt-6 flex-col gap-2">
        <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Request access'}
        </Button>
        <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={onBack}>
          Back to sign in
        </Button>
      </CardFooter>
    </form>
  );
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      setDone(await resetPassword(username.trim(), newPassword));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset the password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <CardContent className="flex flex-col gap-4 text-center">
        <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{done}</p>
        <Button type="button" className="w-full bg-gray-900 hover:bg-gray-800" onClick={onBack}>
          Go to sign in
        </Button>
      </CardContent>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="fp-user">Username</Label>
            <Input
              id="fp-user"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="fp-pass">New password (min 6 characters)</Label>
              <Button
                type="button"
                variant="link"
                onClick={() => setShowPassword((s) => !s)}
                className="ml-auto h-auto p-0 text-sm"
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </div>
            <Input
              id="fp-pass"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              autoComplete="new-password"
            />
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>
      </CardContent>
      <CardFooter className="mt-6 flex-col gap-2">
        <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800" disabled={isSubmitting}>
          {isSubmitting ? 'Resetting...' : 'Reset password'}
        </Button>
        <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={onBack}>
          Back to sign in
        </Button>
      </CardFooter>
    </form>
  );
}

function Login({ onLogin }: LoginProps) {
  const [requesting, setRequesting] = useState(false);
  const [forgotten, setForgotten] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Enter both username and password.');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const user = await login(username, password);
      onLogin(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#3d0d0d] to-[#1a0505] px-4">
      <div className="flex w-full max-w-sm flex-col items-center">
        <Card className="w-full">
          <CardHeader className="flex flex-col items-center text-center">
            <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-3xl">
              🦸
            </div>
            <CardTitle>{APP_NAME}</CardTitle>
            <CardDescription>{APP_TAGLINE}</CardDescription>
          </CardHeader>

          {requesting ? (
            <RequestAccessForm onBack={() => setRequesting(false)} />
          ) : forgotten ? (
            <ForgotPasswordForm onBack={() => setForgotten(false)} />
          ) : (
          <form onSubmit={handleSubmit}>
            <CardContent>
              <div className="flex flex-col gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                  />
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password</Label>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setShowPassword((s) => !s)}
                      className="ml-auto h-auto p-0 text-sm"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="mt-6 flex-col gap-2">
              <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800" disabled={isSubmitting}>
                {isSubmitting ? 'Assembling...' : 'Assemble'}
              </Button>
              <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setRequesting(true)}>
                New here? Request access
              </Button>
              <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setForgotten(true)}>
                Forgot password?
              </Button>
            </CardFooter>
          </form>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Login;
